import { CheckCircle2, TriangleAlert } from 'lucide-react';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

type CallStatus =
  | 'idle'
  | 'in_progress'
  | 'success'
  | 'failed_terminal'
  | 'failed'
  | 'waiting_info';

interface ClaimCallBasicProps {
  callStatus: CallStatus;
  isCallOutcomeReady: boolean;
  latestBland: any | null;
  startCallError?: string | null;
  isStartingCall?: boolean;
  onRetryCall: () => void;
  onBackToReview: () => void;
  onManualCall: () => void;
  manualCallAvailable: boolean;
  lastRunError?: string | null;
  /** When true, disables starting/retrying Voice AI and shows disclaimer. */
  isVoiceAIDisabled?: boolean;
  /** Optional reason shown to the user when Voice AI is disabled. */
  voiceAIDisabledReason?: string;
}

export const ClaimCallBasic: React.FC<ClaimCallBasicProps> = ({
  callStatus,
  isCallOutcomeReady,
  latestBland,
  startCallError,
  isStartingCall,
  onRetryCall,
  onBackToReview,
  onManualCall,
  manualCallAvailable,
  lastRunError,
  isVoiceAIDisabled,
  voiceAIDisabledReason,
}) => {
  const outcome = useMemo(() => {
    if (!latestBland) return null;
    const meta = latestBland.finch_metadata || {};
    const parts: string[] = [];
    if (meta.claim_number) parts.push(`Claim ${meta.claim_number}`);
    if (meta.adjuster_name) parts.push(`${meta.adjuster_name}`);
    if (meta.adjuster_phone) parts.push(`${meta.adjuster_phone}`);
    return parts.join(' • ');
  }, [latestBland]);

  return (
    <div className="space-y-3">
      {callStatus === 'idle' && (
        <div className="rounded-md border bg-white p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900">
              No call has been initiated
            </div>
            <div className="text-xs text-gray-600">
              Start a Voice AI call to open the claim.
            </div>
            {startCallError && (
              <div className="mt-2 text-xs text-red-700">{startCallError}</div>
            )}
            {isVoiceAIDisabled && (
              <div
                className="mt-2 text-sm text-red-700 font-medium"
                role="note"
              >
                {voiceAIDisabledReason ||
                  'Voice AI Call is currently disabled.'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {manualCallAvailable && (
              <Button
                size="sm"
                variant="outline"
                onClick={onManualCall}
                aria-label="Call carrier manually"
              >
                Call manually
              </Button>
            )}
            <Button
              onClick={onRetryCall}
              aria-busy={isStartingCall}
              disabled={!!isStartingCall || !!isVoiceAIDisabled}
              title={
                isVoiceAIDisabled
                  ? voiceAIDisabledReason || 'Voice AI Call is disabled'
                  : undefined
              }
              aria-disabled={!!isVoiceAIDisabled}
            >
              {isStartingCall ? 'Starting…' : 'Start Voice AI Call'}
            </Button>
          </div>
        </div>
      )}

      {callStatus === 'in_progress' && (
        <div className="rounded-md border bg-white p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Placing call…
              </div>
              <div className="text-xs text-gray-600">
                We’ll post updates here.
              </div>
            </div>
            <div className="flex items-center gap-2">
              {manualCallAvailable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onManualCall}
                  aria-label="Call carrier manually"
                >
                  Call manually
                </Button>
              )}
              <Button disabled>Calling…</Button>
            </div>
          </div>
          <div className="mt-2">
            <Progress value={isCallOutcomeReady ? 100 : 66} className="w-56" />
            {isCallOutcomeReady && !latestBland && (
              <div className="text-xs text-gray-600 mt-1">
                Processing outcome…
              </div>
            )}
          </div>
        </div>
      )}

      {(callStatus === 'success' ||
        callStatus === 'failed_terminal' ||
        callStatus === 'failed') && (
        <div className="rounded-md border p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              {callStatus === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden />
              ) : (
                <TriangleAlert className="h-4 w-4 text-amber-600" aria-hidden />
              )}
              <span>
                {callStatus === 'success'
                  ? 'Call completed'
                  : 'Call didn’t complete'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {manualCallAvailable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onManualCall}
                  aria-label="Call carrier manually"
                >
                  Call manually
                </Button>
              )}
              {callStatus !== 'success' && (
                <Button
                  onClick={onRetryCall}
                  aria-label="Retry Voice AI Call"
                  disabled={!!isVoiceAIDisabled}
                  title={
                    isVoiceAIDisabled
                      ? voiceAIDisabledReason || 'Voice AI Call is disabled'
                      : undefined
                  }
                  aria-disabled={!!isVoiceAIDisabled}
                >
                  Retry Voice AI Call
                </Button>
              )}
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-800">
            <p
              className="leading-relaxed truncate"
              title={latestBland?.summary || ''}
            >
              {latestBland?.summary ||
                (callStatus === 'success'
                  ? 'Call completed.'
                  : 'Partial progress achieved.')}
            </p>
            {lastRunError && (
              <div className="text-xs text-red-700 mt-1" role="alert">
                {lastRunError}
              </div>
            )}
            {outcome && (
              <div
                className="text-xs text-gray-600 mt-1 truncate"
                title={outcome}
              >
                {outcome}
              </div>
            )}
            {isVoiceAIDisabled && callStatus !== 'success' && (
              <div
                className="text-sm text-red-700 font-medium mt-2"
                role="note"
              >
                {voiceAIDisabledReason ||
                  'Voice AI Call is currently disabled.'}
              </div>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-800">
            <div className="font-medium mb-1">Next Steps</div>
            <ul className="list-disc pl-5 space-y-1">
              {callStatus === 'success' ? (
                <>
                  <li>Confirm final details are correct.</li>
                  <li>Proceed with the next section to send the LoR.</li>
                </>
              ) : (
                <>
                  <li>Verify policy or client details if uncertain.</li>
                  <li>Retry during business hours.</li>
                </>
              )}
            </ul>
          </div>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
              onClick={onBackToReview}
            >
              Back to Review
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
